import { useState, useEffect, useCallback } from 'react';
import { getParameterDisplayName } from '../utils/parameterDisplayNames';
import { getImagingOptionTranslation } from '../utils/imagingParameterOptionsVi';

/**
 * Custom hook for managing imaging form language state and translations
 * Provides functions to translate parameter names and options while maintaining
 * English data storage for backend compatibility
 */
export const useImagingLanguage = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Load language preference from sessionStorage on hook initialization
  useEffect(() => {
    const savedLanguage = sessionStorage.getItem('carePlanningImagingLanguage');
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'vi')) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Handle language change
  const changeLanguage = useCallback((language) => {
    if (language === 'en' || language === 'vi') {
      setCurrentLanguage(language);
      sessionStorage.setItem('carePlanningImagingLanguage', language);
    }
  }, []);

  // Get translated parameter name
  const getTranslatedParameterName = useCallback((parameterKey) => {
    try {
      return getParameterDisplayName(parameterKey, currentLanguage);
    } catch (error) {
      console.warn(`Translation not found for parameter: ${parameterKey}`, error);
      // Fallback to English if Vietnamese translation fails
      try {
        return getParameterDisplayName(parameterKey, 'en');
      } catch (fallbackError) {
        console.error(`No translation found for parameter: ${parameterKey}`, fallbackError);
        return parameterKey; // Return the key itself as last resort
      }
    }
  }, [currentLanguage]);

  // Get translated option value for display
  const getTranslatedOption = useCallback((option) => {
    if (currentLanguage === 'vi') {
      return getImagingOptionTranslation(option);
    }
    return option;
  }, [currentLanguage]);

  // Translate an array of options for display
  const getTranslatedOptions = useCallback((options) => {
    if (!Array.isArray(options)) return options;
    
    if (currentLanguage === 'vi') {
      return options.map(option => getImagingOptionTranslation(option));
    }
    return options;
  }, [currentLanguage]);

  // Get placeholder text with translation
  const getTranslatedPlaceholder = useCallback((parameterName) => {
    const translatedName = getTranslatedParameterName(parameterName);
    if (currentLanguage === 'vi') {
      return `Nhập ${translatedName}`;
    }
    return `Enter ${translatedName}`;
  }, [currentLanguage, getTranslatedParameterName]);

  // Check if current language is Vietnamese
  const isVietnamese = currentLanguage === 'vi';

  // Check if current language is English
  const isEnglish = currentLanguage === 'en';

  return {
    currentLanguage,
    changeLanguage,
    getTranslatedParameterName,
    getTranslatedOption,
    getTranslatedOptions,
    getTranslatedPlaceholder,
    isVietnamese,
    isEnglish
  };
};

export default useImagingLanguage;
